import React, { useEffect } from 'react';

import { TableHead, TableBody, Table } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Skeleton from '@material-ui/lab/Skeleton';
import styled from 'styled-components';
import { StyledBox, StyledTypography } from 'styles/common';

import { getColorFromKey } from 'constants/helper';
import { PlusIcon2 } from 'constants/icons';

import NewEditableTagRow, {
  getLeftPadding,
  getLeftPosition,
  horizontalLineWidth,
  StyledHorizontalLine,
  StyledVerticalLine,
} from './NewEditableTagRow';
import { StyledTableRow, StyledTableCell, HeaderTableCell, SkeletonContainer } from './styles';
import useTableState from './useTableState';

const getVerticalLines = (nestingLevel, levelsForWhichLinesToBeDrawn) => {
  if (nestingLevel === 0) return null;

  return (
    <>
      {levelsForWhichLinesToBeDrawn.map((num) => (
        <StyledVerticalLine left={`-${getLeftPosition(nestingLevel + 1 - num)}`} />
      ))}
      <StyledVerticalLine left={`-${horizontalLineWidth}px`} bottom="50%" />
      <StyledHorizontalLine width={`${horizontalLineWidth}px`} />
    </>
  );
};

const CircularIcon = styled(Avatar)`
  width: 24px;
  height: 24px;

  background-color: transparent;
  color: ${(props) => getColorFromKey(props.color)};

  border: ${(props) => `1px solid ${getColorFromKey(props.color)}`};
`;

const StyledPlusIcon = styled(PlusIcon2)`
  height: 10px;
  width: 10px;

  fill: ${(props) => props.theme.palette.primary.main};
`;

const ExpandedTableWithinCard = ({
  tableTag,
  enableParentExpansion,
  disableParentExpansion,
  nestingLevel = 1,
  showExpandedCard,
  modalView,
  enableCreation,
  selectTag,
  levelsForWhichLinesToBeDrawn,
  editingTags,
  renamingTags,
  setEditingTags,
  setRenamingTags,
  timePeriodsList = [],
  timePeriodData = {},
  selectedTimeBoundPeriod,
  searchString = '',
  selectedObjective = null,
  selectedObjectiveHierarchy = [],
  isScrolledToSelected = false,
  setIsScrolledToSelected = () => {},
}) => {
  const {
    tags,
    loading,
    newTag,
    showTagCreationRow,
    getAllTags,
    updateTag,
    createNewTag,
    enableTagEditing,
    disableEditingForAllTags,
    handleToggleActive,
    displayNewTagCreationRow,
    hideNewTagCreationRow,
    toggleTagEditing,
    toggleTagRenaming,
    disableExpansion,
    enableExpansion,
  } = useTableState({
    tableTag,
    enableParentExpansion,
    setEditingTags,
    setRenamingTags,
  });

  useEffect(() => {
    if (showExpandedCard) getAllTags();
  }, [showExpandedCard]);

  return (
    <StyledTableRow>
      <StyledTableCell border-bottom="none" padding="none" colSpan={5}>
        <>
          {loading ? (
            <Loader />
          ) : (
            <Table>
              <TableHeader modalView={modalView} />
              <TableBody>
                {showTagCreationRow ? (
                  <NewEditableTagRow
                    tag={{ ...newTag, timeBoundDetails: selectedTimeBoundPeriod, isEditing: true }} // onCancel={hideNewTagCreationRow}
                    onUpdate={createNewTag}
                    isExpandable
                    isEditing
                    toggleEditing={toggleTagEditing}
                    toggleTagRenaming={toggleTagRenaming}
                    nestingLevel={nestingLevel}
                    hasChildTags={false}
                    canHaveChildTags={false}
                    getFocus
                    levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
                    editingTags={editingTags}
                    renamingTags={renamingTags}
                    setEditingTags={setEditingTags}
                    setRenamingTags={setRenamingTags}
                    modalView={modalView}
                    timePeriodData={timePeriodData}
                    timePeriodsList={timePeriodsList}
                    refreshCurrentPage={getAllTags}
                    searchString={searchString}
                    selectedTimeBoundPeriod={selectedTimeBoundPeriod}
                  />
                ) : enableCreation ? (
                  <CreateSubObjectiveRow
                    nestingLevel={nestingLevel}
                    levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
                    displayNewTagCreationRow={displayNewTagCreationRow}
                  />
                ) : null}
                {showExpandedCard &&
                  tags.map((tag, index) => (
                    <NewEditableTagRow
                      key={tag.objectiveId}
                      isEditing={tag.isEditing}
                      isRenaming={tag.isRenaming}
                      toggleEditing={toggleTagEditing}
                      toggleRenaming={toggleTagRenaming}
                      onActiveToggle={handleToggleActive}
                      onUpdate={updateTag}
                      tag={tag}
                      isExpandable
                      nestingLevel={nestingLevel}
                      hasChildTags={tag.hasChildTags}
                      canHaveChildTags
                      disableExpansion={() => disableExpansion(tag.objectiveId)}
                      enableExpansion={() => enableExpansion(tag.objectiveId)}
                      modalView={modalView}
                      getFocus={false}
                      enableCreation={enableCreation}
                      selectTag={selectTag}
                      timePeriodData={timePeriodData}
                      timePeriodsList={timePeriodsList}
                      levelsForWhichLinesToBeDrawn={
                        index === tags.length - 1
                          ? [...levelsForWhichLinesToBeDrawn]
                          : [...levelsForWhichLinesToBeDrawn, nestingLevel]
                      }
                      isLastRow={index === tags.length - 1}
                      editingTags={editingTags}
                      renamingTags={renamingTags}
                      setEditingTags={setEditingTags}
                      setRenamingTags={setRenamingTags}
                      refreshCurrentPage={getAllTags}
                      searchString={searchString}
                      selectedTimeBoundPeriod={selectedTimeBoundPeriod}
                      selectedObjective={selectedObjective}
                      selectedObjectiveHierarchy={selectedObjectiveHierarchy}
                      isScrolledToSelected={isScrolledToSelected}
                      setIsScrolledToSelected={setIsScrolledToSelected}
                    />
                  ))}
              </TableBody>
            </Table>
          )}
        </>
      </StyledTableCell>
    </StyledTableRow>
  );
};

const Loader = () => (
  <SkeletonContainer>
    <Skeleton width="50%" />
    <Skeleton width="40%" />
  </SkeletonContainer>
);

const TableHeader = ({ modalView = false }) => (
  <TableHead>
    <HeaderTableCell padding="0" width="40%" />
    <HeaderTableCell padding="0" width="25%" />
    {!modalView && <HeaderTableCell padding="0" width="25%" />}
    <HeaderTableCell padding="0" width="10%" />
  </TableHead>
);

const CreateSubObjectiveRow = ({ nestingLevel, levelsForWhichLinesToBeDrawn, displayNewTagCreationRow }) => (
  <StyledTableRow>
    <StyledTableCell paddingtop="0" paddingBottom="0" paddingLeft={getLeftPadding(nestingLevel)} colSpan={4} border>
      <StyledCellContainer>
        {getVerticalLines(nestingLevel, levelsForWhichLinesToBeDrawn)}
        <StyledBox
          display="inline-flex"
          alignItems="center"
          onClick={displayNewTagCreationRow}
          style={{ cursor: 'pointer' }}
        >
          <CircularIcon color="primary">
            <StyledPlusIcon />
          </CircularIcon>
          <StyledTypography2 color="primary" leftGutter={3}>
            Create New Sub Objective
          </StyledTypography2>
        </StyledBox>
      </StyledCellContainer>
    </StyledTableCell>
  </StyledTableRow>
);

const StyledCellContainer = styled.div`
  height: 62px;
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledTypography2 = styled(StyledTypography)`
  line-height: 3;
  font-size: 0.875rem;
`;

export default ExpandedTableWithinCard;
